/* ============================================================
 * 工单 — C 端 API
 *
 * 接口对齐后端 §6.4（14 个 C 端接口）+ §6.10 字段规格 + 前端对接指南 §11。
 *   - 详情接口 A6.1 申请详情 `GET /app/order/{orderNo}/applicants/{creatorUserId}`
 *
 * 通用约定：
 *   - 大厅列表：AjaxResult（{code, msg, data: {rows, total}}）→ ApiResponse<{rows, total}>
 *   - 我的工单 / 我的发单（v2）：{code, msg, data: OrderListAggregateVO} → ApiResponse<OrderListAggregateVO<T, K>>
 *   - 详情 / 动作：{code, msg, data} → ApiResponse<T>
 *   - 错误统一抛 ApiError（前端用 getErrorMessage 取 msg）
 * ============================================================ */

import http from '@/api/client'
import type { ApiResponse } from '@/types/api'
import type {
  ApplicantDetailVO,
  ClientKpiBlock,
  ClientOrderItemVO,
  CreatorKpiBlock,
  CreatorOrderItemVO,
  OrderApplicantVO,
  OrderDetailVO,
  OrderDictVO,
  OrderHallItemVO,
  OrderHallQuery,
  OrderListAggregateVO,
  OrderStatus,
  ApplyOrderReq,
  AssignOrderReq,
  PublishOrderReq,
  RejectDeliveryReq,
  SaveDraftReq,
  SubmitDraftReq,
  SubmitProgressReq,
} from '@/types/order'

/* ============================================================
 * 大厅
 * ============================================================ */

/** 大厅列表（匿名可看）；query 全部可选，空 query = 默认排序最新 + 第 1 页 10 条。 */
export function getOrderHall(
  query: OrderHallQuery = {},
): Promise<ApiResponse<{ rows: OrderHallItemVO[]; total: number }>> {
  return http
    .get<ApiResponse<{ rows: OrderHallItemVO[]; total: number }>>('/app/order/hall', {
      params: query,
    })
    .then((r) => r.data)
}

/* ============================================================
 * 发单表单字典（匿名可看）
 *   - 字段命名 `{ value, label, description }`（与 `/app/apply/{role}/dict` 一致）
 *   - `value: CreatorField` 严格类型与创作者入驻 fieldList 同源（后端 §6.9）
 *   - 类型在 `@/types/order.ts`；API 函数在此就近
 * ============================================================ */

/** GET /app/order/dict — 匿名可看。三个 list 都是稳定的可枚举值，前端做 10 分钟 sessionStorage 缓存。 */
export function getOrderDict(): Promise<ApiResponse<OrderDictVO>> {
  return http.get<ApiResponse<OrderDictVO>>('/app/order/dict').then((r) => r.data)
}

/* ============================================================
 * 详情
 * ============================================================ */

/** 订单详情（匿名可看；按登录态返回 myRole）。 */
export function getOrderDetail(orderNo: string): Promise<ApiResponse<OrderDetailVO>> {
  return http.get<ApiResponse<OrderDetailVO>>(`/app/order/${orderNo}`).then((r) => r.data)
}

/* ============================================================
 * 发单 / 草稿（需企业认证）
 * ============================================================ */

/** 一步发单（POST /app/order/publish）；返回新订单号。 */
export function publishOrder(payload: PublishOrderReq): Promise<ApiResponse<string>> {
  return http.post<ApiResponse<string>>('/app/order/publish', payload).then((r) => r.data)
}

/** 保存草稿（POST /app/order/draft/save）。
 *  - orderNo 空/不传 = 新建草稿；非空 = 更新该草稿
 *  - 宽松校验（仅校验已填字段合法）
 *  - 返回草稿订单号
 */
export function saveOrderDraft(payload: SaveDraftReq): Promise<ApiResponse<string>> {
  return http.post<ApiResponse<string>>('/app/order/draft/save', payload).then((r) => r.data)
}

/** 提交草稿审核（POST /app/order/draft/submit）；
 *  - DRAFT → REVIEWING，严格校验必填后置 PENDING
 *  - 返回订单号
 */
export function submitOrderDraft(payload: SubmitDraftReq): Promise<ApiResponse<string>> {
  return http.post<ApiResponse<string>>('/app/order/draft/submit', payload).then((r) => r.data)
}

/* ============================================================
 * 动作（按状态 / 角色）
 * ============================================================ */

/** 取消订单（发单方 + REVIEWING/OPEN）。 */
export function cancelOrder(orderNo: string): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>(`/app/order/${orderNo}/cancel`).then((r) => r.data)
}

/** 创作者申请接单（OPEN + 创作者认证）。 */
export function applyOrder(
  orderNo: string,
  payload: ApplyOrderReq = {},
): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>(`/app/order/${orderNo}/apply`, payload).then((r) => r.data)
}

/** 候选池（发单方 + OPEN）。 */
export function getOrderApplicants(orderNo: string): Promise<ApiResponse<OrderApplicantVO[]>> {
  return http
    .get<ApiResponse<OrderApplicantVO[]>>(`/app/order/${orderNo}/applicants`)
    .then((r) => r.data)
}

/** 申请详情（发单方 + OPEN；含简历/作品/报价明细/工作状态/微信/完成单数）。 */
export function getApplicantDetail(
  orderNo: string,
  creatorUserId: number,
): Promise<ApiResponse<ApplicantDetailVO>> {
  return http
    .get<ApiResponse<ApplicantDetailVO>>(`/app/order/${orderNo}/applicants/${creatorUserId}`)
    .then((r) => r.data)
}

/** 企业选人（OPEN → ASSIGNED；目标必须已申请）。 */
export function assignOrder(
  orderNo: string,
  payload: AssignOrderReq,
): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>(`/app/order/${orderNo}/assign`, payload).then((r) => r.data)
}

/** 提交交付 / 存草稿（接单方 + ASSIGNED/PROCESSING；submit=true → DELIVERED）。
 *  - files: submit=true 必填 ≥1；submit=false 可空（存草稿）
 */
export function submitOrderProgress(
  orderNo: string,
  payload: SubmitProgressReq,
): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>(`/app/order/${orderNo}/progress`, payload).then((r) => r.data)
}

/** 驳回交付（发单方 + DELIVERED → PROCESSING；remark 必填）。 */
export function rejectOrderDelivery(
  orderNo: string,
  payload: RejectDeliveryReq,
): Promise<ApiResponse<null>> {
  return http
    .post<ApiResponse<null>>(`/app/order/${orderNo}/reject-delivery`, payload)
    .then((r) => r.data)
}

/** 验收通过（发单方 + DELIVERED → SETTLED；钱包校验 → 内部转账）。
 *  - 后端企业钱包不足返回 500 + 「企业钱包余额不足，请联系客服」，前端不做补缴。
 */
export function acceptOrder(orderNo: string): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>(`/app/order/${orderNo}/accept`).then((r) => r.data)
}

/* ============================================================
 * 我的工单 / 我的发单
 * ============================================================ */

/** 我的工单（创作者视角）；status 可选（OrderStatus 或空 = 全部）。
 *  v2 响应为 ApiResponse<OrderListAggregateVO<…, CreatorKpiBlock>>：
 *  取 `resp.data.rows / resp.data.total / resp.data.kpi / resp.data.counts`。
 */
export function getMyCreatorOrders(
  params: { status?: OrderStatus; pageNum?: number; pageSize?: number } = {},
): Promise<ApiResponse<OrderListAggregateVO<CreatorOrderItemVO, CreatorKpiBlock>>> {
  return http
    .get<ApiResponse<OrderListAggregateVO<CreatorOrderItemVO, CreatorKpiBlock>>>(
      '/app/order/my/creator',
      { params },
    )
    .then((r) => r.data)
}

/** 我的发单（企业视角）；status 可选（OrderStatus 或空 = 全部，含 DRAFT）。
 *  v2 响应为 ApiResponse<OrderListAggregateVO<…, ClientKpiBlock>>。
 *  字段对齐 `前端对接指南.md` A12：`status / keyword / category / minBudget / maxBudget / pageNum / pageSize`。
 */
export function getMyClientOrders(
  params: {
    status?: OrderStatus
    keyword?: string
    category?: string
    minBudget?: number
    maxBudget?: number
    pageNum?: number
    pageSize?: number
  } = {},
): Promise<ApiResponse<OrderListAggregateVO<ClientOrderItemVO, ClientKpiBlock>>> {
  return http
    .get<ApiResponse<OrderListAggregateVO<ClientOrderItemVO, ClientKpiBlock>>>(
      '/app/order/my/client',
      { params },
    )
    .then((r) => r.data)
}
